import { type ReactNode, useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Archive, Loader2, MoreHorizontal, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
} from '@/components/ui/drawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import type { ChatConversation } from '@/lib/chat/types';

interface ConversationHistoryProps {
  conversations: ChatConversation[];
  selectedConversationId: string | null;
  isLoading: boolean;
  onSelectConversation: (conversationId: string) => void;
  onRenameConversation: (conversation: ChatConversation) => Promise<void>;
  onArchiveConversation?: (conversation: ChatConversation) => Promise<void>;
  isMobile?: boolean;
  children: ReactNode;
}

function getConversationTitle(conversation: ChatConversation): string {
  return conversation.title?.trim() || 'Untitled conversation';
}

interface TimeGroup {
  label: string;
  items: ChatConversation[];
}

function formatUpdatedAt(updatedAt: string): string {
  const parsed = new Date(updatedAt);
  if (Number.isNaN(parsed.getTime())) return '';
  return formatDistanceToNow(parsed, { addSuffix: true });
}

function groupByTimePeriod(conversations: ChatConversation[]): TimeGroup[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const threeDaysAgo = new Date(today.getTime() - 3 * 24 * 60 * 60 * 1000);
  const oneMonthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

  const groups: TimeGroup[] = [
    { label: 'Today', items: [] },
    { label: 'Last 3 days', items: [] },
    { label: 'Last month', items: [] },
    { label: 'Older', items: [] },
  ];

  for (const conv of conversations) {
    const date = new Date(conv.updated_at);
    if (date >= today) groups[0].items.push(conv);
    else if (date >= threeDaysAgo) groups[1].items.push(conv);
    else if (date >= oneMonthAgo) groups[2].items.push(conv);
    else groups[3].items.push(conv);
  }

  return groups.filter(g => g.items.length > 0);
}

export function ConversationHistory({
  conversations,
  selectedConversationId,
  isLoading,
  onSelectConversation,
  onRenameConversation,
  onArchiveConversation,
  isMobile = false,
  children,
}: ConversationHistoryProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filteredConversations = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return conversations;

    return conversations.filter(conversation => {
      const title = getConversationTitle(conversation).toLowerCase();
      return title.includes(normalizedQuery);
    });
  }, [conversations, query]);

  const groups = useMemo(() => groupByTimePeriod(filteredConversations), [filteredConversations]);

  const content = (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between px-4 pb-3 pt-4">
        <p className="text-sm font-semibold text-neutral-800">Conversation history</p>
        <Badge variant="secondary" className="rounded-full bg-neutral-100 text-[11px] font-medium text-neutral-500">
          {conversations.length}
        </Badge>
      </div>

      <div className="px-3 pb-3">
        <Input
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder="Search conversations..."
          className="h-9 border-neutral-200 bg-white text-sm"
        />
      </div>

      <Separator />

      <ScrollArea className="min-h-0 flex-1">
        <div className="py-2">
          {isLoading && conversations.length === 0 && (
            <div className="flex items-center gap-2 px-4 py-4 text-xs text-neutral-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading conversations...
            </div>
          )}

          {!isLoading && conversations.length === 0 && (
            <p className="px-4 py-4 text-xs text-neutral-500">No conversations yet.</p>
          )}

          {!isLoading && conversations.length > 0 && filteredConversations.length === 0 && (
            <p className="px-4 py-4 text-xs text-neutral-500">No matching conversations.</p>
          )}

          {groups.map(group => (
            <div key={group.label}>
              <p className="px-4 pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
                {group.label}
              </p>

              <div className="space-y-0.5 px-2">
                {group.items.map(conversation => (
                  <div
                    key={conversation.id}
                    className={cn(
                      'group relative rounded-md border border-transparent transition-colors',
                      conversation.id === selectedConversationId
                        ? 'border-neutral-200 bg-neutral-100'
                        : 'hover:border-neutral-200 hover:bg-neutral-50',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        onSelectConversation(conversation.id);
                        setOpen(false);
                      }}
                      className="w-full px-2.5 py-2.5 pr-10 text-left"
                    >
                      <p
                        className={cn(
                          'truncate text-sm',
                          conversation.id === selectedConversationId ? 'font-semibold text-neutral-900' : 'font-medium text-neutral-700',
                        )}
                      >
                        {getConversationTitle(conversation)}
                      </p>
                      <p className="mt-0.5 text-xs text-neutral-500">
                        Updated {formatUpdatedAt(conversation.updated_at)}
                      </p>
                    </button>

                    <div className="absolute right-1.5 top-1.5">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-600"
                            onClick={event => event.stopPropagation()}
                          >
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent align="end" sideOffset={6}>
                          <DropdownMenuItem
                            onClick={event => {
                              event.stopPropagation();
                              void onRenameConversation(conversation);
                            }}
                          >
                            <Pencil className="mr-2 h-3.5 w-3.5" />
                            Rename
                          </DropdownMenuItem>

                          {onArchiveConversation && (
                            <DropdownMenuItem
                              onClick={event => {
                                event.stopPropagation();
                                void onArchiveConversation(conversation);
                              }}
                            >
                              <Archive className="mr-2 h-3.5 w-3.5" />
                              Archive
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>{children}</DrawerTrigger>
        <DrawerContent className="h-[min(85vh,40rem)] p-0">
          {content}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[min(34rem,calc(100vw-1.5rem))] p-0"
      >
        <div className="h-[min(72vh,36rem)]">
          {content}
        </div>
      </PopoverContent>
    </Popover>
  );
}
